/**
 * DIV-031: on-demand jQuery UI dialog. See jquery-ui-dialog-entry.ts.
 *
 * `withDialog(fn)` runs `fn` once `.dialog()` exists on the bundle's jQuery: at
 * once when it already does (the common case — `warmDialog()` fetches the chunk
 * right after the calculator becomes interactive, long before any error), else
 * after the chunk loads. A failed load is retried on the next call.
 */
import $ from "jquery";
import { appState } from "../state/app-state";

let loading: Promise<void> | undefined;

export function dialogReady(): boolean {
  return typeof ($.fn as any).dialog === "function";
}

export function ensureDialog(): Promise<void> {
  if (dialogReady()) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${appState.cdnURL}/jquery-ui-dialog.js`;
    script.onload = () => (dialogReady() ? resolve() : reject(new Error("jquery-ui-dialog.js loaded but registered nothing")));
    script.onerror = () => reject(new Error("jquery-ui-dialog.js failed to load"));
    document.head.appendChild(script);
  }).catch((error) => {
    loading = undefined;
    throw error;
  });
  return loading;
}

/** Fire-and-forget: have the chunk in place before a shopper can hit an error path. */
export function warmDialog(): void {
  ensureDialog().catch(() => undefined);
}

/** Run `fn` with `.dialog()` available — synchronously when it already is. */
export function withDialog(fn: () => void): void {
  if (dialogReady()) {
    fn();
    return;
  }
  ensureDialog().then(fn, () => undefined);
}
